import React, { useState } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { AppPwdTextField, PwdFieldType } from '../../customComponents/AppPwdTextField';
import { FormTitle } from '../../customComponents/FormTitle';
import PrimaryButton from '../../components/PrimaryButton';
import CenteredLayout from '../../layout/CenteredLayout';
import PageContainerLayout from '../../layout/PageContainerLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import { changePassword } from '../../lib/api/user/userApi';
import { useLang } from '../../context/LangContext';
import { useUserAuth } from '../../context/UserAuthContext';
import { useDevice } from '../../context/DeviceContext';

// 다국어 텍스트 객체
const changePasswordTexts = {
  ko: {
    title: '비밀번호 변경',
    newPasswordSetting: '새 비밀번호 설정',
    changePasswordButton: '비밀번호 변경',
    changing: '변경 중...',
    
    // 토스트 메시지
    passwordChangeSuccess: '비밀번호가 성공적으로 변경되었습니다.',
    passwordChangeFailed: '비밀번호 변경에 실패했습니다. 다시 시도해주세요.'
  },
  en: {
    title: 'Change Password',
    newPasswordSetting: 'Set New Password',
    changePasswordButton: 'Change Password',
    changing: 'Changing...',
    
    // 토스트 메시지
    passwordChangeSuccess: 'Password has been successfully changed.',
    passwordChangeFailed: 'Failed to change password. Please try again.'
  }
};

// ChangePasswordContainer styles are now handled by PageContainerLayout

const FlexPageContainer = styled.div<{ $minHeight: string }>`
  display: flex;
  flex-direction: column;
  /* height: 100%; */
  min-height: ${({ $minHeight }) => $minHeight};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const NextButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: auto;
  padding: 24px 0 0 0;
`;



function ChangePassword() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const { isLoggedIn } = useUserAuth();
  const device = useDevice();
  const texts = changePasswordTexts[lang];
  
  // 앱바 높이를 임의로 설정 (모바일: 60px, 웹: 90px)
  const appbarHeight = device === 'mobile' ? 120 : 250;
  const minHeight = `calc(100vh - ${appbarHeight}px)`;
  
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [isNewPasswordValid, setIsNewPasswordValid] = useState(false);
  const [isNewPasswordConfirmValid, setIsNewPasswordConfirmValid] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  // 로그인 상태 확인
  React.useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  // 에러 메시지 파싱 함수 (FindPassword에서 가져옴)
  const parseErrorMessage = (error: any, defaultMessage: string): string => {
    // 중첩된 에러 구조 파싱
    if (error?.data?.error?.details?.details && Array.isArray(error.data.error.details.details)) {
      // validation 에러의 상세 메시지 추출
      const validationError = error.data.error.details.details[0];
      if (validationError?.message) {
        return validationError.message;
      }
    }
    
    if (error?.data?.error?.customMessage) {
      return error.data.error.customMessage;
    }
    
    if (error?.data?.message) {
      return error.data.message;
    }
    
    if (error?.message) {
      return error.message;
    }
    
    return defaultMessage;
  };

  const handleBack = () => {
    navigate('/edit-profile');
  };



  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewPassword(e.target.value);
  };

  const handleNewPasswordConfirmChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewPasswordConfirm(e.target.value);
  };

  const handleComplete = async () => {
    try {
      setIsChanging(true);
      
      const response = await changePassword({
        password: newPassword.trim()
      } as any);

      // 응답 데이터의 statusCode 확인 후 메시지 처리
      if (response.data.statusCode === 200) {
        toast.success(texts.passwordChangeSuccess);
        navigate('/edit-profile');
      } else {
        // 에러 응답 처리
        const errorMessage = response.data.message || texts.passwordChangeFailed;
        toast.error(errorMessage);
      }
      
    } catch (error: any) {
      devError('비밀번호 변경 실패:', error);
      
      const errorMessage = parseErrorMessage(error, texts.passwordChangeFailed);
      toast.error(errorMessage);
    } finally {
      setIsChanging(false);
    }
  };

  const isFormValid = isNewPasswordValid && isNewPasswordConfirmValid && 
                     newPassword.trim() !== '' && newPasswordConfirm.trim() !== '';

  return (
    <CenteredLayout>
      <ProjectAppbar
        title={texts.title}
        showBackButton={true}
        onBack={handleBack}
      />
      <PageContainerLayout>
        <FlexPageContainer $minHeight={minHeight}>
          <FormGroup>
            <FormTitle>{texts.newPasswordSetting}</FormTitle>

            <AppPwdTextField
              value={newPassword}
              onChange={handleNewPasswordChange}
              fieldType={PwdFieldType.PASSWORD}
              onValidationChange={setIsNewPasswordValid}
              language={lang}
              autoComplete="new-password"
              autoFocus={true}
            />

            <AppPwdTextField
              value={newPasswordConfirm}
              onChange={handleNewPasswordConfirmChange}
              fieldType={PwdFieldType.PASSWORD_CONFIRM}
              onValidationChange={setIsNewPasswordConfirmValid}
              originalPassword={newPassword}
              language={lang}
              autoComplete="new-password"
            />
          </FormGroup>

          <NextButtonContainer>
            <PrimaryButton
              onClick={handleComplete}
              disabled={!isFormValid || isChanging}
            >
              {isChanging ? texts.changing : texts.changePasswordButton}
            </PrimaryButton>
          </NextButtonContainer>
        </FlexPageContainer>
      </PageContainerLayout>
    </CenteredLayout>
  );
}

export default ChangePassword;
